/** @format */

/**
 * Service Worker Cache Update Listener
 *
 * Listens for BroadcastChannel messages from the service worker's BroadcastUpdatePlugin.
 * When the SW updates a cached API response in the background, this listener
 * invalidates the corresponding React Query cache so the UI updates transparently.
 *
 * This solves the "double caching" problem where:
 * 1. SW serves stale cached data immediately (good for UX)
 * 2. SW fetches fresh data in background and updates its cache
 * 3. Without this listener, React Query never learns about the fresh data
 * 4. With this listener, React Query refetches and gets the now-fresh SW cache
 */

import type { QueryClient } from "@tanstack/react-query";

// Workbox BroadcastUpdatePlugin message format
interface CacheUpdateMessage {
  type: "CACHE_UPDATED";
  meta: string; // "workbox-broadcast-update"
  payload: {
    cacheName: string;
    updatedURL: string;
  };
}

/**
 * Maps a URL to React Query cache keys that should be invalidated.
 * Returns an array of query key prefixes to invalidate.
 */
function urlToQueryKeys(url: string): unknown[][] {
  const urlObj = new URL(url, window.location.origin);
  const pathname = urlObj.pathname;

  // Extract URL parameters for parameterized routes
  const searchParams = urlObj.searchParams;

  const keysToInvalidate: unknown[][] = [];

  // Season standings: /api/standings/?series=X&year=Y
  if (pathname.includes("/standings/") || pathname === "/api/standings/") {
    const series = searchParams.get("series");
    const year = searchParams.get("year");
    if (series && year) {
      keysToInvalidate.push(["season_standings", series, year]);
    } else {
      // Invalidate all standings if no specific params
      keysToInvalidate.push(["season_standings"]);
    }
  }

  // Race standings: /api/races/{raceId}/standings/
  const raceStandingsMatch = pathname.match(/\/api\/races\/([^/]+)\/standings\//);
  if (raceStandingsMatch) {
    keysToInvalidate.push(["race_standings", raceStandingsMatch[1]]);
  }

  // Races list: /api/races/?series=X&year=Y
  if (pathname === "/api/races/" || pathname === "/api/races") {
    const series = searchParams.get("series");
    const year = searchParams.get("year");
    if (series && year) {
      keysToInvalidate.push(["races", series, year]);
    } else {
      // Invalidate all races lists
      keysToInvalidate.push(["races"]);
    }
  }

  // Race detail: /api/races/{raceId}/
  const raceDetailMatch = pathname.match(/\/api\/races\/([^/]+)\/$/);
  if (
    raceDetailMatch &&
    !pathname.includes("/standings/") &&
    !pathname.includes("/entries/")
  ) {
    keysToInvalidate.push(["race", raceDetailMatch[1]]);
  }

  // Series: /api/series/
  if (pathname.includes("/api/series/")) {
    keysToInvalidate.push(["series"]);
  }

  // Seasons: /api/seasons/
  if (pathname.includes("/api/seasons/")) {
    keysToInvalidate.push(["seasons"]);
  }

  // Next/Previous race
  if (pathname.includes("/next_race/")) {
    keysToInvalidate.push(["next_race"]);
  }
  if (pathname.includes("/previous_race/")) {
    keysToInvalidate.push(["previous_race"]);
  }

  // Run views: /api/run_views/
  if (pathname.includes("/api/run_views/")) {
    keysToInvalidate.push(["runViews"]);
  }

  // Drivers: /api/drivers/ or /api/drivers/{id}/
  if (pathname.match(/\/api\/drivers\//)) {
    keysToInvalidate.push(["drivers"]);
  }

  // Driver standings: /api/drivers/{id}/standings/
  const driverStandingsMatch = pathname.match(/\/api\/drivers\/([^/]+)\/standings\//);
  if (driverStandingsMatch) {
    keysToInvalidate.push(["driver_standings", driverStandingsMatch[1]]);
  }

  // Run sub-resources: /api/runs/{runId}/vehicles/, pitstops/, points/, etc.
  // Query keys follow ["run", runId, resource, ...scope] pattern via usePlaybackQuery
  const runSubResourceMatch = pathname.match(
    /\/api\/runs\/([^/]+)\/(vehicles|pitstops|points|vehicle_configs|vehicle_crews|weather|tracks)\//
  );
  if (runSubResourceMatch) {
    const [, runId, resource] = runSubResourceMatch;
    keysToInvalidate.push(["run", runId, resource]);
  }

  // Schedule: /api/schedule/
  if (pathname.includes("/api/schedule/")) {
    keysToInvalidate.push(["schedule"]);
  }

  return keysToInvalidate;
}

let broadcastChannel: BroadcastChannel | null = null;
let isInitialized = false;

/**
 * Initialize the cache update listener.
 * Should be called once when the app mounts.
 *
 * @param queryClient - The React Query client instance
 */
export function initCacheUpdateListener(queryClient: QueryClient): void {
  // Only initialize once
  if (isInitialized) {
    return;
  }

  // BroadcastChannel is not available in all browsers (e.g., Safari < 15.4)
  if (typeof BroadcastChannel === "undefined") {
    return;
  }

  // Create channel to receive messages from service worker
  broadcastChannel = new BroadcastChannel("workbox");

  broadcastChannel.addEventListener(
    "message",
    (event: MessageEvent<CacheUpdateMessage>) => {
      // Validate message format
      if (
        event.data?.type !== "CACHE_UPDATED" ||
        event.data?.meta !== "workbox-broadcast-update"
      ) {
        return;
      }

      const { updatedURL, cacheName } = event.data.payload;

      // Log in development for debugging
      if (import.meta.env.DEV) {
        console.info(
          `[CacheUpdateListener] Cache updated: ${updatedURL} (cache: ${cacheName})`
        );
      }

      // Map URL to query keys and invalidate
      const keysToInvalidate = urlToQueryKeys(updatedURL);

      for (const queryKey of keysToInvalidate) {
        if (import.meta.env.DEV) {
          console.info(`[CacheUpdateListener] Invalidating query key:`, queryKey);
        }

        // Invalidate matching queries - this triggers a background refetch
        // The refetch will hit the SW cache which now has fresh data
        // Fire-and-forget: we don't need to await the invalidation
        void queryClient.invalidateQueries({
          queryKey,
          // Don't immediately refetch if the query isn't currently being observed
          // This prevents unnecessary network requests for unmounted components
          refetchType: "active",
        });
      }
    }
  );

  isInitialized = true;
}

/**
 * Cleanup the cache update listener.
 * Should be called when the app unmounts (rarely needed in practice).
 */
export function cleanupCacheUpdateListener(): void {
  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }
  isInitialized = false;
}
